import MuiSelect, { SelectChangeEvent } from '@mui/material/Select';
import React, { useState } from 'react';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

type OptionId = string;

interface OptionMap<T> {
  readonly [id: OptionId]: T;
}

type Props<T> = {
  readonly label: string;
  readonly options: T[];
  readonly selectOptionId?: (object: T) => OptionId;
  readonly selectOptionLabel?: (object: T) => string;
  readonly defaultOptionId?: OptionId;
  readonly onSelect: (object: T) => void;
  readonly isLoading?: boolean;
  readonly disabled?: boolean;
  readonly required?: boolean;
};

const defaultOptionIdSelector = (object: any) => object.id;
const defaultOptionLabelSelector = (object: any) => object.label;

const SingleSelect = <T extends object>(props: Props<T>) => {
  const {
    label,
    options,
    selectOptionId = defaultOptionIdSelector,
    selectOptionLabel = defaultOptionLabelSelector,
    defaultOptionId,
    onSelect,
    isLoading = false,
    disabled = false,
    required = false,
  } = props;

  const [selectedOptionId, setSelectedOptionId] = useState<OptionId | undefined>(defaultOptionId);

  const optionMap: OptionMap<T> = options
    .reduce((acc, option) => {
      const optionId = selectOptionId(option);

      return {
        ...acc,
        [optionId]: option,
      };
    }, {} as OptionMap<T>);

  const handleChange = (event: SelectChangeEvent) => {
    const optionId = event.target.value as OptionId;

    setSelectedOptionId(optionId);
    onSelect(optionMap[optionId]);
  };

  const SelectIcon = isLoading ? HourglassEmptyIcon : ArrowDropDownIcon;

  return (
    <FormControl variant='standard' fullWidth>
      <InputLabel id='demo-select-small'>{label}</InputLabel>
      <MuiSelect
        IconComponent={SelectIcon}
        labelId='demo-select-small'
        id='demo-select-small'
        value={selectedOptionId || ''}
        onChange={handleChange}
        disabled={disabled}
        required={required}
      >
        {options.map(option => (
          <MenuItem key={selectOptionId(option)} value={selectOptionId(option)}>
            {selectOptionLabel(option)}
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  );
};

export default SingleSelect;
